import React, { PureComponent } from 'react';
import { IntlProvider } from 'react-intl';
import PropTypes from 'prop-types';
import { ThemeProvider } from 'styled-components';
import { graphql, compose, withApollo } from 'react-apollo';
import { Route, Switch, Redirect, withRouter } from 'react-router-dom';
import axiosRetry from 'axios-retry';
import axios from 'axios';
import { isBrowserSupported } from 'utils/browserSupport';
import PrivateRoute from 'common/PrivateRoute';
import {
	lStorage,
	cookiesHelper,
	BaseStyles,
	notifyToast,
	withAppContext,
} from 'modules/utils';
import { locales } from 'modules/utils/cp3/locales';
import LazyLoadable from 'modules/utils/cp3/hoc/LazyLoadable';

import queryString from 'query-string';
import { PHP_URL, AVAILABLE_PATHS, LOGOUT_URL, GLOBAL_PROPERTIES_URL } from 'utils/constants';
import { decodeLavuToken } from 'utils/jwtUtils';

// [BEGIN] MUTATIONS [BEGIN]
// [END] MUTATIONS [END]

import Authentication from 'Authentication';
import ThirdPartyAuth from 'Authentication/ThirdPartyAuth';
import UserMutations from './mutations/user.mutations';
import UnsupportedBrowser from './UnsupportedBrowser';

const ControlPanel = LazyLoadable(() => import('./ControlPanel'));

const theme = {
	fontFamily: '"Open Sans", sans-serif',
	fontColorLight: '#ffffff',
	fontColorDark: '#393939',
	white: '#f4f4f2',
	offWhite: '#ebebeb',
	gray: '#8b8b8b',
	grayLight: '#bdbdbd',
	grayDark: '#2b2b2b',
	grayDarker: '#1b1b1b',
	primary: '#99bc08',
	primaryLight: '#aecd37',
	sidebarBg: '#373737',
	sidebarBgLight: '#130a36',
	sidebarBgDark: 'rgba(19, 10, 54, 0.64)',
};

theme.sidebarFontColor = theme.fontColorLight;
theme.activeAnchorTag = theme.fontColorDark;
theme.sidebarBorder = theme.grayDark;
theme.bg = theme.offWhite;

export class App extends PureComponent {
	currentUserStorage = lStorage.hasData({ key: 'current-user' });

	state = {
		isAuthenticated:
			this.currentUserStorage && this.currentUserStorage.hasOwnProperty('user'),
		User: null,
	};

	static propTypes = {
		client: PropTypes.object.isRequired,
		logoutUser: PropTypes.func.isRequired,
		updateContext: PropTypes.func.isRequired,
		history: PropTypes.shape({
			listen: PropTypes.func,
		}),
		languageCode: PropTypes.string,
		currentUser: PropTypes.object,
	};

	componentDidMount() {
		// baseStyles if been defined in the lavu-utils folder
		// this will inject styles globally through the application
		// using injectGlobal from styles-components
		setTimeout(() => BaseStyles(), 0);

		// Setting the isAuthenticated if the current_user is present
		window.addEventListener('message', e => {
			// change to prod and development url dynamic
			if (PHP_URL.includes(e.origin)) {
				this.logout();
			}
		});
	}

	static getDerivedStateFromProps() {
		// Applying basetyle when component updates.
		setTimeout(() => BaseStyles(), 0);

		if (cookiesHelper.get('LAVU_TOKEN')) {
			return { isAuthenticated: true };
		}

		return null;
	}

	logout = async (user, redirectToLogin = true) => {
		const { client, logoutUser } = this.props;

		if (!user) {
			this.redirectToLogin();
			return;
		}

		try {
			await logoutUser({
				variables: {
					dataname: user.originalDataname,
					username: user.username,
				},
			});
			try {
				await this.logoutPHP();
			} catch (error) {
				// eslint-disable-next-line no-console
				console.log('logout error ', error);
			}
			this.resetStorageAndCookies(client, redirectToLogin);
		} catch (error) {
			if (error.graphQLErrors[0]) {
				// eslint-disable-next-line no-console
				console.log('==== error', error.graphQLErrors[0].message.toString());
				// eslint-disable-next-line consistent-return
				return error.graphQLErrors[0].message.toString();
			}
		}
	};

	logoutPHP = async () => {
		await axios.post(LOGOUT_URL, {}, { withCredentials: true });
	};

	resetStorageAndCookies(client, redirectToLogin = true) {
		// code to reset window._env_ variables
		const envReactAppVars = new Array("REACT_APP_PHP_URL","REACT_APP_INVENTORY_GRAPHQL_URL","REACT_APP_LAVU_API_URL","REACT_APP_REPORTS_URL","REACT_APP_AWS_END_POINT"); 
		for (const key in window._env_){
			if(envReactAppVars.includes(key)){
				delete window._env_[key];
			}
		}

		window._env_loaded=false;
			client.clearStore();
		client.cache.reset();
		const switchCP4LastSeen = localStorage.getItem(
			'SWITCH_CP4_MODAL_LAST_DISMISSED'
		);

		localStorage.clear();
		cookiesHelper.remove('LAVU_TOKEN');
		cookiesHelper.remove('CP3_ENABLED');
        cookiesHelper.remove('XSRF-TOKEN');

		// Persist data about users who have dismissed the "Switch to CP4" modal
		if (switchCP4LastSeen) {
			localStorage.setItem(
				'SWITCH_CP4_MODAL_LAST_DISMISSED',
				switchCP4LastSeen
			);
		}

		if (redirectToLogin) {
			this.redirectToLogin();
		}
	}

	redirectToLogin = () => {
		const { updateContext } = this.props;

		this.setState({ isAuthenticated: false }, () => {
			updateContext({
				isPrintable: false,
				location: {},
				chainLocations: [],
			});
			return <Redirect to="/login" />;
		});
	};

	renderContent = () => {
		const { isAuthenticated } = this.state;
		const { client, currentUser } = this.props;
		const parsedQuery = queryString.parse(window.location.search);

		if (!isBrowserSupported()) {
			const { user } = lStorage.get({ key: 'current-user' });
			this.logout(user, false);
			return <UnsupportedBrowser loading={!currentUser} />;
		}

		return (
			<PrivateRoute
				path="/"
				component={ControlPanel}
				logout={this.logout}
				isAuthenticated={isAuthenticated}
				client={client}
				parsedQuery={parsedQuery}
			/>
		);
	};

	//function to set env variable  
	globalProperties = () => {
		axiosRetry(axios, { retries: 3 });
		const token = cookiesHelper.get('LAVU_TOKEN');
		axios
		.get(`${GLOBAL_PROPERTIES_URL}`, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
			responseType: 'json',
		})
		.then(({ data: res }) => {
			// set the enviroment variables after fetch
			if (!(window?._env_)) {
				window._env_ = {};
			}
			for (const key in res.cp3Env){
				window._env_[key] = res.cp3Env[key];
			}

			window._env_loaded=true;
		})
		.catch((error) => {
			console.log('error loading properties ', error);
		});	
	}
	render() {
		const { isAuthenticated } = this.state;
		const { client, languageCode } = this.props;
		const { pathname: windowPathname } = window.location;
		const { newLogin, ...otherQuery } = queryString.parse(
			window.location.search
		);
		if (newLogin) {
			this.resetStorageAndCookies(client);

			return (
				<Redirect
					to={{
						pathname: '/login',
						search: queryString.stringify(otherQuery),
					}}
				/>
			);
		}

		if (lStorage.get({ key: '@session-logout' })) {
			const storageHasErrorMessage = lStorage.hasData({
				key: '@error-message',
			});
			const defaultMessage = 'Your session has expired.';
			const message = storageHasErrorMessage
				? lStorage.get({ key: '@error-message' })
				: defaultMessage;

			lStorage.remove({ key: '@session-logout' });
			lStorage.remove({ key: '@error-message' });
			notifyToast({
				kind: 'warning',
				position: 'top-center',
				autoClose: true,
				message,
			});
		}

		const lavuToken = cookiesHelper.get('LAVU_TOKEN');
		const tokenUser = decodeLavuToken();

		if (tokenUser) {
			const { user } = lStorage.get({ key: 'current-user' });

			if (!user) {
				lStorage.set({ key: 'current-user', value: { user: tokenUser } });
			} else if (
				user.dataname !== tokenUser.dataname ||
				user.username !== tokenUser.username
			) {
				lStorage.set({ key: 'current-user', value: { user: tokenUser } });
			}

			// load global properties if _env_ is not loaded yet
			if(!window._env_loaded)
			{
				this.globalProperties();
			}

			if (!tokenUser.isNewControlPanelActive) {
				window.location.href = `${PHP_URL}?mode=home_home`;

				return null;
			}
		}

		// this will check if the user is login
		// if is not and if is not in the user is not in the login path
		// it will redirect to the login path
		if (
			!lavuToken &&
			// Remove last `/` from pathname if present
			!AVAILABLE_PATHS.includes(windowPathname.replace(/\/$/, ''))
		) {
			return <Redirect to="/login" />;
		}

		return (
			<ThemeProvider theme={theme}>
				<IntlProvider
					locale={languageCode}
					defaultLocale="en"
					messages={locales[languageCode]}
				>
					<Switch>
						<Route path="/oauth/authorize" component={ThirdPartyAuth} />
						{isAuthenticated ? (
							this.renderContent()
						) : (
							<Authentication logout={this.logout} />
						)}
						<Route render={() => <p>Page not found</p>} />
					</Switch>
				</IntlProvider>
			</ThemeProvider>
		);
	}
}

// If you don't have the key in locastorage skip the query
// and redirect back to login
export default compose(
	withApollo,
	withRouter,
	graphql(UserMutations.logoutUser, { name: 'logoutUser' }),
	withAppContext(
		({ updateContext, isManageUser, languageCode, currentUser }) => ({
			updateContext,
			isManageUser,
			languageCode,
			currentUser,
		})
	)
)(App);
